import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Clock, StickyNote, Link2, BellOff } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { ETAPAS, FASES_HISTORICO, TIPO_LABEL } from "@/lib/stages";

export function ContactFormModal({
  open, onClose, form, setForm, onSubmit, saving, isEdit, editContact,
  historico, novoRegistro, setNovoRegistro, onAddHistorico, responsaveis, intencoes,
  onToggleOptOut,
}) {
  const [crossCampaigns, setCrossCampaigns] = useState([]);

  useEffect(() => {
    if (isEdit && editContact?.id) {
      api.get(`/contacts/${editContact.id}/cross-campaigns`).then((res) => setCrossCampaigns(res.data.campaigns || [])).catch(() => setCrossCampaigns([]));
    } else {
      setCrossCampaigns([]);
    }
  }, [isEdit, editContact?.id]);

  const intencoesList = intencoes && intencoes.length ? intencoes : ["Corte", "Coloração", "Tratamento", "Outro"];
  const isCustom = form.intencao && !intencoesList.includes(form.intencao) && form.intencao !== "Outro";
  const showOutro = isCustom || form.intencao === "Outro";
  const selectVal = showOutro ? "Outro" : form.intencao || "";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 text-base font-semibold">
            {isEdit ? "Editar contato" : "Novo contato"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" style={{ minHeight: 0 }}>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="novo" className="text-xs">{TIPO_LABEL.novo}</SelectItem>
                    <SelectItem value="base" className="text-xs">{TIPO_LABEL.base}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Responsável</Label>
                <Input value={form.responsavel} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} placeholder="Nome" className="h-8 text-xs" list="resp-form-list" />
                <datalist id="resp-form-list">
                  {responsaveis.map((r) => <option key={r} value={r} />)}
                </datalist>
              </div>
            </div>

            <div>
              <Label className="text-xs">Nome *</Label>
              <Input data-testid="contact-name-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome" className="h-8 text-xs" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Telefone *</Label>
                <Input data-testid="contact-phone-input" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(11) 99999-9999" className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Instagram</Label>
                <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@usuario" className="h-8 text-xs" />
              </div>
            </div>

            <div>
              <Label className="text-xs">DM Instagram</Label>
              <Input data-testid="dm-instagram-input" value={form.dm_instagram} onChange={(e) => setForm({ ...form, dm_instagram: e.target.value })} placeholder="Link ou texto da DM enviada" className="h-8 text-xs" />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Origem</Label>
                <Input value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })} placeholder="Instagram" className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Intenção</Label>
                <Select value={selectVal} onValueChange={(v) => setForm({ ...form, intencao: v })}>
                  <SelectTrigger data-testid="intencao-select" className="h-8 text-xs"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {intencoesList.map((i) => (<SelectItem key={i} value={i} className="text-xs">{i}</SelectItem>))}
                    {!intencoesList.includes("Outro") && <SelectItem value="Outro" className="text-xs">Outro</SelectItem>}
                  </SelectContent>
                </Select>
                {showOutro && (
                  <Input data-testid="intencao-outro-input" value={form.intencao === "Outro" ? "" : form.intencao} onChange={(e) => setForm({ ...form, intencao: e.target.value || "Outro" })} placeholder="Digite a intenção" className="h-8 text-xs mt-1" />
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Retornar em</Label>
                <Input type="date" value={form.retornar_em || ""} onChange={(e) => setForm({ ...form, retornar_em: e.target.value })} className="h-8 text-xs" data-testid="input-retornar-em" />
              </div>
              <div>
                <Label className="text-xs">Próxima ação</Label>
                <Input value={form.proxima_acao || ""} onChange={(e) => setForm({ ...form, proxima_acao: e.target.value })} placeholder="Ligar após o almoço" className="h-8 text-xs" data-testid="input-proxima-acao" />
              </div>
            </div>

            <Button type="submit" data-testid="contact-submit-btn" disabled={saving} className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-600 h-8 text-xs font-semibold disabled:opacity-50">
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </form>

          {isEdit && editContact && (
            <>
              <div className="mt-5 pt-4 border-t border-zinc-800">
                <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="opt-out-row">
                  <input type="checkbox" checked={!!editContact.opt_out} onChange={(e) => onToggleOptOut(e.target.checked)} className="accent-amber-500" data-testid="opt-out-toggle" />
                  <BellOff className="h-3.5 w-3.5 text-red-400" />
                  <span>Não quer mais contato</span>
                </label>
                {editContact.opt_out && (
                  <p className="text-[10px] text-zinc-500 mt-1">Este telefone não receberá mensagens e será ignorado em futuras importações.</p>
                )}
              </div>

              <div className="mt-5 pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="h-4 w-4 text-amber-400" />
                  <span className="text-sm font-medium text-zinc-100">Anotação</span>
                </div>
                <p className="text-xs text-zinc-400 whitespace-pre-wrap bg-zinc-900 rounded-lg p-3 min-h-[40px]" data-testid="edit-note-display">
                  {editContact.anotacao || "Sem anotação. Use o botão de nota no card para adicionar."}
                </p>
              </div>

              {crossCampaigns.length > 0 && (
                <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="cross-campaigns">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="h-4 w-4 text-blue-400" />
                    <span className="text-sm font-medium text-zinc-100">Também está em</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {crossCampaigns.map((camp) => (
                      <span key={camp.id} className="text-xs bg-blue-500/10 text-blue-400 px-2.5 py-1 rounded-md">{camp.nome}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2 mb-3">
                  <Send className="h-4 w-4 text-zinc-500" />
                  <span className="text-sm font-medium text-zinc-100">Registrar ação</span>
                </div>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Select value={novoRegistro.fase} onValueChange={(v) => setNovoRegistro({ ...novoRegistro, fase: v })}>
                      <SelectTrigger data-testid="historico-fase-select" className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FASES_HISTORICO.map((f) => (<SelectItem key={f.id} value={f.id} className="text-xs">{f.short}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input data-testid="historico-responsavel-input" value={novoRegistro.responsavel} onChange={(e) => setNovoRegistro({ ...novoRegistro, responsavel: e.target.value })} placeholder="Responsável" className="h-8 w-28 text-xs" list="resp-hist-list" />
                    <datalist id="resp-hist-list">{responsaveis.map((r) => <option key={r} value={r} />)}</datalist>
                    <Button type="button" data-testid="historico-add-btn" onClick={onAddHistorico} size="sm" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 h-8 text-xs">
                      <Send className="h-3 w-3 mr-1" /> Adicionar
                    </Button>
                  </div>
                  <Textarea data-testid="historico-texto-input" value={novoRegistro.texto} onChange={(e) => setNovoRegistro({ ...novoRegistro, texto: e.target.value })} placeholder="O que foi feito? Mensagem enviada, conversa, etc..." className="text-xs min-h-[60px] resize-none" />
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-zinc-800">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-zinc-500" />
                  <span className="text-sm font-medium text-zinc-100">Histórico</span>
                </div>
                <div className="space-y-2" data-testid="historico-timeline">
                  {historico.length === 0 ? (
                    <p className="text-xs text-zinc-600 text-center py-4">Nenhum registro ainda</p>
                  ) : (
                    [...historico].reverse().map((reg, i) => {
                      if (!reg || typeof reg !== "object") return null;
                      const faseLabel = ETAPAS.find((f) => f.id === reg.fase)?.short || reg.fase || "—";
                      const resp = reg.responsavel || "";
                      let dataStr = "";
                      if (reg.created_at) {
                        try {
                          const d = new Date(reg.created_at);
                          if (!isNaN(d.getTime()) && d.getFullYear() >= 1970 && d.getFullYear() <= 3000) {
                            dataStr = format(d, "dd/MM/yyyy HH:mm", { locale: ptBR });
                          }
                        } catch { /* ignore */ }
                      }
                      return (
                        <div key={reg.id || i} className="bg-zinc-900 rounded-lg p-3" data-testid="historico-record">
                          <div className="flex items-center gap-2 mb-1.5 text-[11px] text-zinc-500">
                            <span className="font-semibold text-zinc-200 bg-zinc-800 px-2 py-0.5 rounded uppercase text-[10px]">{faseLabel}</span>
                            {resp && <><span>—</span><span className="text-zinc-400">{resp}</span></>}
                            {dataStr && <><span>—</span><span>{dataStr}</span></>}
                          </div>
                          <p className="text-xs text-zinc-400 whitespace-pre-wrap leading-relaxed">{reg.texto || ""}</p>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
